import { motion } from "motion/react";
import { FileText, CheckCircle2, Clock, Send } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

export function Apply() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-32 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-7xl mb-6">Apply for Funding</h1>
            <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
              If you are building solutions in education, skills, livelihoods, or rural development, we invite you to apply for financial support from the Immerse foundation.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Process Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-3xl text-center mb-12">Application Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="text-primary" size={28} />
              </div>
              <h3 className="text-lg mb-2">1. Submit Application</h3>
              <p className="text-sm text-muted-foreground">
                Fill out the form with your project details
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="text-primary" size={28} />
              </div>
              <h3 className="text-lg mb-2">2. Review Process</h3>
              <p className="text-sm text-muted-foreground">
                Our team reviews within 2 weeks
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 className="text-primary" size={28} />
              </div>
              <h3 className="text-lg mb-2">3. Verification</h3>
              <p className="text-sm text-muted-foreground">
                Due diligence and site visit if needed
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Send className="text-primary" size={28} />
              </div>
              <h3 className="text-lg mb-2">4. Go Live</h3>
              <p className="text-sm text-muted-foreground">
                Your project gets listed on our platform
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Application Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-8">
                <h2 className="text-3xl mb-8">Application Form</h2>

                <form className="space-y-6">
                  {/* Organization Details */}
                  <div className="space-y-4">
                    <h3 className="text-xl">Organization Details</h3>
                    <div>
                      <Label htmlFor="orgName" className="mb-2">Organization Name *</Label>
                      <Input
                        id="orgName"
                        placeholder="Your NGO/Organization name"
                        className="border border-gray-400 rounded-md p-2"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="regNumber" className="mb-2">Registration Number *</Label>
                        <Input id="regNumber" placeholder="12345/2020" className="border border-gray-400 rounded-md p-2" required />
                      </div>
                      <div>
                        <Label htmlFor="yearEst" className="mb-2">Year Established *</Label>
                        <Input
                          id="yearEst"
                          type="number"
                          placeholder="2015"
                          className="border border-gray-400 rounded-md p-2"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="website" className="mb-2">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        className="border border-gray-400 rounded-md p-2"
                        placeholder="https://yourwebsite.org"
                      />
                    </div>
                  </div>

                  {/* Contact Person */}
                  <div className="space-y-4">
                    <h3 className="text-xl">Contact Person</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contactName" className="mb-2">Full Name *</Label>
                        <Input id="contactName" className="border border-gray-400 rounded-md p-2" required />
                      </div>
                      <div>
                        <Label htmlFor="designation" className="mb-2">Designation *</Label>
                        <Input id="designation" placeholder="Director" className="border border-gray-400 rounded-md p-2"   required />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="mb-2">Email Address *</Label>
                        <Input id="email" type="email" className="border border-gray-400 rounded-md p-2" required />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="mb-2">Phone Number *</Label>
                        <Input id="phone" type="tel" className="border border-gray-400 rounded-md p-2" required />
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-4">
                    <h3 className="text-xl">Project Details</h3>
                    <div>
                      <Label htmlFor="projectTitle" className="mb-2">Project Title *</Label>
                      <Input
                        id="projectTitle"
                        placeholder="Brief, descriptive title"
                        className="border border-gray-400 rounded-md p-2"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="category">Project Category *</Label>
                      <Select required>
                        <SelectTrigger id="category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tourism">
                            Sustainable Tourism
                          </SelectItem>
                          <SelectItem value="agriculture">
                            Agriculture & Farming
                          </SelectItem>
                          <SelectItem value="crafts">
                            Crafts & Artisans
                          </SelectItem>
                          <SelectItem value="education">Education</SelectItem>
                          <SelectItem value="infrastructure">
                            Infrastructure
                          </SelectItem>
                          <SelectItem value="women">
                            Women Empowerment
                          </SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="location" className="mb-2">
                        Project Location *
                      </Label>
                      <Input
                        id="location"
                        placeholder="State, District, Village"
                        className="border border-gray-400 rounded-md p-2"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="description" className="mb-2">
                        Project Description *
                      </Label>
                      <Textarea
                        id="description"
                        rows={6}
                        className="border border-gray-400 rounded-md p-2"
                        placeholder="Describe your project, its objectives, target beneficiaries, and expected outcomes..."
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="fundingNeeded">
                          Funding Needed (₹) *
                        </Label>
                        <Input
                          id="fundingNeeded"
                          type="number"
                          placeholder="500000"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="duration">
                          Project Duration (months) *
                        </Label>
                        <Input
                          id="duration"
                          type="number"
                          placeholder="12"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* UNWTO Goals */}
                  <div className="space-y-4">
                    <h3 className="text-xl">UNWTO Goals Alignment *</h3>
                    <p className="text-sm text-muted-foreground">
                      Select all that apply to your project
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox id="goal8" />
                        <label htmlFor="goal8" className="text-sm cursor-pointer">
                          SDG 8: Decent Work and Economic Growth
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="goal10" />
                        <label htmlFor="goal10" className="text-sm cursor-pointer">
                          SDG 10: Reduced Inequalities
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="goal12" />
                        <label htmlFor="goal12" className="text-sm cursor-pointer">
                          SDG 12: Responsible Consumption and Production
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Documents */}
                  <div className="space-y-4">
                    <h3 className="text-xl">Supporting Documents</h3>
                    <p className="text-sm text-muted-foreground">
                      Please prepare these documents (you'll upload them after
                      initial approval):
                    </p>
                    <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                      <li>NGO Registration Certificate</li>
                      <li>12A/80G Certificate (if applicable)</li>
                      <li>Annual Reports (last 2 years)</li>
                      <li>Detailed Project Proposal</li>
                      <li>Budget Breakdown</li>
                    </ul>
                  </div>

                  {/* Agreement */}
                  <div className="flex items-start space-x-2">
                    <Checkbox id="agreement" required />
                    <label
                      htmlFor="agreement"
                      className="text-sm cursor-pointer leading-relaxed"
                    >
                      I confirm that all information provided is accurate and I
                      agree to provide regular project updates if funded. I
                      understand that Immerse Foundation will conduct due
                      diligence verification.
                    </label>
                  </div>

                  <Button type="submit" size="lg" className="w-full">
                    Submit Application
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6 sticky top-32">
              {/* Guidelines */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl mb-4">Eligibility Criteria</h3>
                  <ul className="space-y-3 text-sm">
                    <li className="flex gap-2">
                      <CheckCircle2
                        className="text-primary flex-shrink-0"
                        size={18}
                      />
                      <span>Registered NGO/Non-profit organization</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle2
                        className="text-primary flex-shrink-0"
                        size={18}
                      />
                      <span>Project aligned with UNWTO Goals 8, 10, or 12</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle2
                        className="text-primary flex-shrink-0"
                        size={18}
                      />
                      <span>Clear impact measurement plan</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle2
                        className="text-primary flex-shrink-0"
                        size={18}
                      />
                      <span>Transparent financial management</span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle2
                        className="text-primary flex-shrink-0"
                        size={18}
                      />
                      <span>Focus on sustainable development</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Contact */}
              <Card className="bg-muted/50">
                <CardContent className="p-6">
                  <h3 className="text-xl mb-3">Need Help?</h3>
                  <p className="text-sm mb-4 text-muted-foreground">
                    Have questions about the application process? We're here to
                    help.
                  </p>
                  <Button variant="outline" className="w-full">
                    Contact Us
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
